import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ShoppingCart,
  Clock,
  ChefHat,
  Plus,
  Minus,
  Tag,
  CheckCircle,
  Truck,
  CreditCard,
  Banknote,
  ArrowRight,
  Loader,
  Percent,
} from "lucide-react";
import {
  addItem,
  decreaseItem,
  setCartList,
} from "../../Redux/State-slice/CartSlice";
import {
  createOrder,
  getDeliveryQuote,
  initiateEpsPayment,
  initiateStripePayment,
  validateCoupon,
  fetchSellerCoupons,
} from "./services";
import AddressPicker from "./AddressPicker";
import useUserRegion from "../../customHooks/useUserRegion";
import { effectivePrice } from "../../utils/price";
import { foodTypeLabel } from "../../services/normalizers";
import "./checkout.css";

const getSellerKey = (item) => item.sellerID || item.sellerId?._id || "unknown";

// Shared with the cart drawer so the two always agree; the order total itself
// is still computed server-side from the catalogue.
const getEffectivePrice = effectivePrice;

const parseTimeToMinutes = (v) => {
  if (v === null || v === undefined) return 0;
  if (typeof v === "number") return v;
  const s = String(v).toLowerCase().trim();
  const day = s.match(/(\d+(?:\.\d+)?)\s*day/);
  if (day) return Math.round(parseFloat(day[1]) * 24 * 60);
  const hr = s.match(/(\d+(?:\.\d+)?)\s*(hour|hr|h)/);
  if (hr) return Math.round(parseFloat(hr[1]) * 60);
  const min = s.match(/(\d+(?:\.\d+)?)\s*(minute|min|m)/);
  if (min) return Math.round(parseFloat(min[1]));
  const n = parseFloat(s);
  return Number.isNaN(n) ? 0 : Math.round(n);
};

export default function CheckOut() {
  const dispatch = useDispatch();
  const location = useLocation();
  const cart = useSelector((s) => s.cart.cartItems);
  const selectedSellerId = location?.state?.sellerId;

  const [paySystemName, setPaySystemName] = useState("cashOnDelivery");
  const [couponPrice, setCouponPrice] = useState(0);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [coupon, setCoupon] = useState("");
  const [totalPrice, setTotalPrice] = useState(0);
  const [subTotal, setSubTotal] = useState(0);
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCouponLoading, setIsCouponLoading] = useState(false);
  const [couponApplied, setCouponApplied] = useState(false);
  const [showCouponsModal, setShowCouponsModal] = useState(false);
  const [sellerCoupons, setSellerCoupons] = useState([]);
  const [sellerCouponsLoading, setSellerCouponsLoading] = useState(false);
  // The API places orders against a saved address id, not an inline address.
  const [addressId, setAddressId] = useState(null);
  const [deliveryWarning, setDeliveryWarning] = useState("");

  const { isBangladesh, loading: regionLoading } = useUserRegion();
  const sym = isBangladesh ? "৳" : "$";

  const sellerIdsInCart = useMemo(
    () => [...new Set((cart || []).map(getSellerKey))],
    [cart],
  );
  const effectiveSellerId = selectedSellerId || sellerIdsInCart[0] || null;

  const filteredCart = useMemo(
    () =>
      effectiveSellerId
        ? (cart || []).filter(
            (item) => getSellerKey(item) === effectiveSellerId,
          )
        : [],
    [cart, effectiveSellerId],
  );

  const groupedCart = useMemo(() => {
    const groups = {};
    filteredCart.forEach((item) => {
      const key = getSellerKey(item);
      if (!groups[key])
        groups[key] = {
          sellerName: item.sellerId?.name || "Unknown Kitchen",
          sellerId: key,
          items: [],
        };
      groups[key].items.push(item);
    });
    return Object.values(groups);
  }, [filteredCart]);

  const estimatedDeliveryMinutes = useMemo(() => {
    if (!filteredCart.length) return 0;
    return filteredCart.reduce(
      (max, item) =>
        Math.max(
          max,
          parseTimeToMinutes(item?.waitingTime) ||
            parseTimeToMinutes(item?.foodOrderBeforeTime) ||
            parseTimeToMinutes(item?.deliveryTime) ||
            parseTimeToMinutes(item?.orderBeforeTime),
        ),
      0,
    );
  }, [filteredCart]);

  const estimatedDeliveryLabel = useMemo(() => {
    if (!estimatedDeliveryMinutes) return "N/A";
    if (estimatedDeliveryMinutes >= 60) {
      const h = estimatedDeliveryMinutes / 60;
      const r = Number.isInteger(h) ? h : h.toFixed(1);
      return `${r} hour${Number(r) > 1 ? "s" : ""}`;
    }
    return `${estimatedDeliveryMinutes} min`;
  }, [estimatedDeliveryMinutes]);

  // The delivery fee is distance-based, so it is quoted by the API for this
  // kitchen/address pair — the same calculation order creation will apply.
  useEffect(() => {
    if (!effectiveSellerId || !addressId) {
      setDeliveryFee(0);
      setDeliveryWarning("");
      return;
    }
    let cancelled = false;
    getDeliveryQuote({ chefId: effectiveSellerId, addressId })
      .then((quote) => {
        if (cancelled || !quote) return;
        setDeliveryFee(Number(quote.deliveryFee) || 0);
        setDeliveryWarning(
          quote.withinRange
            ? ""
            : "This kitchen does not deliver to the selected address.",
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setDeliveryFee(0);
        setDeliveryWarning(err?.message || "");
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveSellerId, addressId]);

  useEffect(() => {
    let s = 0;
    filteredCart.forEach((item) => {
      s += item.foodQty * getEffectivePrice(item);
    });
    setSubTotal(s);
    setCouponPrice(0);
    setCouponApplied(false);
  }, [filteredCart]);

  useEffect(() => {
    setTotalPrice(subTotal + deliveryFee - couponPrice);
  }, [subTotal, deliveryFee, couponPrice]);

  const applyCoupon = async (couponCode = coupon) => {
    const trimmedCoupon = couponCode.trim();
    if (!trimmedCoupon) {
      toast.warning("Please enter a coupon code.");
      return false;
    }
    if (!effectiveSellerId) {
      toast.warning("No seller selected.");
      return false;
    }
    try {
      setIsCouponLoading(true);
      const res = await validateCoupon({
        code: trimmedCoupon,
        chefId: effectiveSellerId,
        subtotal: subTotal,
      });
      if (res?.status) {
        const { discountAmount } = res.data;
        setCoupon(trimmedCoupon);
        setCouponPrice(discountAmount);
        setCouponApplied(true);
        toast.success(
          `Coupon applied! You save ${sym}${discountAmount.toFixed(2)}`,
        );
        return true;
      } else {
        toast.error(res?.message || "Invalid coupon.");
        return false;
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to apply coupon.",
      );
      setCouponPrice(0);
      setCouponApplied(false);
      return false;
    } finally {
      setIsCouponLoading(false);
    }
  };

  const handleCoupon = async () => {
    await applyCoupon(coupon);
  };

  const openCouponsModal = async () => {
    if (!effectiveSellerId) {
      toast.warning("No seller selected.");
      return;
    }

    setShowCouponsModal(true);
    try {
      setSellerCouponsLoading(true);
      const coupons = await fetchSellerCoupons(effectiveSellerId);
      setSellerCoupons(Array.isArray(coupons) ? coupons : []);
    } catch {
      setSellerCoupons([]);
      toast.error("Failed to load available coupons.");
    } finally {
      setSellerCouponsLoading(false);
    }
  };

  const handleSelectCoupon = async (code) => {
    const applied = await applyCoupon(code);
    if (applied) {
      setShowCouponsModal(false);
    }
  };

  /**
   * The API prices the order itself, so the payload carries only the customer's
   * choices. `paymentMethod` is just "cod" vs "online" — which gateway handles
   * an online order is decided by the initiate call that follows.
   */
  const buildPayload = () => ({
    addressId,
    items: filteredCart.map((item) => ({
      foodId: item?._id,
      quantity: Number(item?.foodQty) || 0,
    })),
    couponCode: couponApplied && coupon ? coupon : undefined,
    notes: additionalInfo || undefined,
    paymentMethod: paySystemName === "cashOnDelivery" ? "cod" : "online",
  });

  const handleSubmitOrder = async () => {
    if (!filteredCart.length) {
      toast.warning("No order items found.");
      return;
    }
    if (!addressId) {
      toast.warning("Please choose a delivery address.");
      return;
    }
    try {
      setIsLoading(true);
      const res = await createOrder(buildPayload());
      if (res?.status) {
        try {
          const remaining = (cart || []).filter(
            (it) => getSellerKey(it) !== effectiveSellerId,
          );
          dispatch(setCartList(remaining));
          localStorage.setItem("cartList", JSON.stringify(remaining));
        } catch {
          /* best-effort */
        }

        if (paySystemName === "cashOnDelivery") {
          toast.success(res.message || "Order placed successfully!");
          window.location.href = "/my-orders";
          return;
        }
        const orderId = res.data?._id;
        if (!orderId) {
          toast.error("Order created but failed to get order ID for payment.");
          return;
        }
        const payRes = isBangladesh
          ? await initiateEpsPayment(orderId)
          : await initiateStripePayment(orderId);
        if (payRes?.status && payRes.data?.redirectUrl) {
          window.location.href = payRes.data.redirectUrl;
          return;
        }
        toast.error(payRes?.message || "Failed to initiate payment.");
        window.location.href = "/my-orders";
      } else {
        toast.error(res?.message || "Failed to create order");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to create order");
    } finally {
      setIsLoading(false);
    }
  };

  const btnLabel = isLoading
    ? "Processing…"
    : regionLoading
      ? "Loading…"
      : paySystemName === "cashOnDelivery"
        ? "Place Order"
        : isBangladesh
          ? "Pay with EPS"
          : "Pay with Stripe";

  return (
    <div className="home-content-shell page-navbar-space pb-5">
      <div className="co-layout">
        {/* ── LEFT: cart items ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Header */}
          <div className="co-card">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <div>
                <h5
                  style={{
                    fontWeight: 800,
                    fontSize: 18,
                    margin: "0 0 3px",
                    color: "#0f172a",
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  Your Cart
                </h5>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      fontSize: 12,
                      color: "#9ca3af",
                      fontFamily: "'Inter', sans-serif",
                    }}
                  >
                    <ShoppingCart size={13} />
                    {filteredCart.length} item
                    {filteredCart.length !== 1 ? "s" : ""}
                  </span>
                  {estimatedDeliveryLabel !== "N/A" && (
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        fontSize: 12,
                        color: "#9ca3af",
                        fontFamily: "'Inter', sans-serif",
                      }}
                    >
                      <Clock size={13} /> Est. {estimatedDeliveryLabel}
                    </span>
                  )}
                </div>
              </div>
              <Link
                to="/all-food"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "8px 16px",
                  borderRadius: 8,
                  border: "1.5px solid #e5e7eb",
                  background: "#f9fafb",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#374151",
                  textDecoration: "none",
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                <Plus size={13} /> Add More
              </Link>
            </div>
          </div>

          {/* Items */}
          {filteredCart.length === 0 ? (
            <div
              className="co-card"
              style={{
                textAlign: "center",
                padding: "48px 20px",
                color: "#9ca3af",
                fontFamily: "'Inter', sans-serif",
              }}
            >
              <ShoppingCart
                size={36}
                color="#e5e7eb"
                style={{ marginBottom: 12 }}
              />
              <p style={{ fontSize: 14, margin: 0 }}>Your cart is empty.</p>
              <Link
                to="/all-food"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  marginTop: 14,
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#0d6efd",
                  textDecoration: "none",
                }}
              >
                Browse food <ArrowRight size={13} />
              </Link>
            </div>
          ) : (
            groupedCart.map((group) => (
              <div key={group.sellerId} className="co-card">
                <div className="co-seller-hdr">
                  <ChefHat size={14} color="#0d6efd" />
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#1e3a5f",
                      fontFamily: "'Inter', sans-serif",
                    }}
                  >
                    {group.sellerName}
                  </span>
                </div>

                {group.items.map((item, idx) => {
                  const effPrice = getEffectivePrice(item);
                  const hasDisc =
                    item.discountType &&
                    item.discountType !== "none" &&
                    item.discountValue > 0;
                  return (
                    <div key={item._id || idx} className="co-item">
                      <img
                        src={item.image || "/Assets/Img/Recipe/imgHub_1_1.png"}
                        alt={item.name}
                        className="co-item-img"
                        onError={(e) => {
                          e.target.src = "/Assets/Img/Recipe/imgHub_1_1.png";
                        }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p
                          style={{
                            margin: "0 0 2px",
                            fontWeight: 700,
                            fontSize: 14,
                            color: "#111827",
                            fontFamily: "'Inter', sans-serif",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {item.name}
                        </p>
                        {item.type && (
                          <p
                            style={{
                              margin: "0 0 4px",
                              fontSize: 11,
                              color: "#9ca3af",
                              fontFamily: "'Inter', sans-serif",
                            }}
                          >
                            {foodTypeLabel(item.type)}
                          </p>
                        )}
                        <div className="co-qty">
                          <button
                            className="co-qty-btn"
                            onClick={() => dispatch(decreaseItem(item))}
                          >
                            <Minus size={12} />
                          </button>
                          <span className="co-qty-val">{item.foodQty}</span>
                          <button
                            className="co-qty-btn"
                            onClick={() => dispatch(addItem(item))}
                          >
                            <Plus size={12} />
                          </button>
                        </div>
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <p
                          style={{
                            margin: 0,
                            fontWeight: 800,
                            fontSize: 14,
                            color: "#0d6efd",
                            fontFamily: "'Inter', sans-serif",
                          }}
                        >
                          {sym}
                          {(effPrice * item.foodQty).toFixed(0)}
                        </p>
                        {hasDisc && (
                          <p
                            style={{
                              margin: 0,
                              fontSize: 11,
                              color: "#9ca3af",
                              textDecoration: "line-through",
                              fontFamily: "'Inter', sans-serif",
                            }}
                          >
                            {sym}
                            {(item.price * item.foodQty).toFixed(0)}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))
          )}

          {/* Additional info */}
          {filteredCart.length > 0 && (
            <div className="co-card">
              <p
                style={{
                  margin: "0 0 8px",
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#9ca3af",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                Special Instructions
              </p>
              <textarea
                rows={3}
                placeholder="Any dietary preferences, allergies, or notes for the kitchen…"
                value={additionalInfo}
                onChange={(e) => setAdditionalInfo(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1.5px solid #e5e7eb",
                  outline: "none",
                  resize: "vertical",
                  fontSize: 13,
                  fontFamily: "'Inter', sans-serif",
                  color: "#374151",
                  background: "#f9fafb",
                  boxSizing: "border-box",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#0d6efd")}
                onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
              />
            </div>
          )}
        </div>

        {/* ── RIGHT: summary ── */}
        <div className="co-summary">
          {/* Delivery address — required by the API to place an order */}
          <AddressPicker selectedId={addressId} onSelect={setAddressId} />

          {deliveryWarning && (
            <div
              style={{
                padding: "10px 12px",
                borderRadius: 10,
                background: "#fef3c7",
                color: "#92400e",
                fontSize: 12,
                fontFamily: "'Inter', sans-serif",
              }}
            >
              {deliveryWarning}
            </div>
          )}

          {/* Order summary */}
          <div className="co-card">
            <p
              style={{
                margin: "0 0 14px",
                fontWeight: 800,
                fontSize: 16,
                color: "#0f172a",
                fontFamily: "'Inter', sans-serif",
              }}
            >
              Order Summary
            </p>

            <div className="co-summary-row">
              <span style={{ color: "#6b7280" }}>Subtotal</span>
              <span style={{ fontWeight: 600, color: "#111827" }}>
                {sym}
                {subTotal.toFixed(0)}
              </span>
            </div>
            <div className="co-summary-row">
              <span style={{ color: "#6b7280" }}>Delivery fee</span>
              <span style={{ fontWeight: 600, color: "#111827" }}>
                {deliveryFee > 0 ? `${sym}${deliveryFee}` : "Free"}
              </span>
            </div>
            {couponPrice > 0 && (
              <div className="co-summary-row">
                <span
                  style={{
                    color: "#059669",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <Tag size={12} /> Coupon discount
                </span>
                <span style={{ fontWeight: 600, color: "#059669" }}>
                  −{sym}
                  {couponPrice.toFixed(0)}
                </span>
              </div>
            )}

            <div
              style={{ borderTop: "1.5px solid #f1f5f9", margin: "12px 0" }}
            />

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span
                style={{
                  fontWeight: 800,
                  fontSize: 15,
                  color: "#0f172a",
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                Total
              </span>
              <span
                style={{
                  fontWeight: 900,
                  fontSize: 22,
                  color: "#0d6efd",
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                {sym}
                {totalPrice.toFixed(0)}
              </span>
            </div>
          </div>

          {/* Coupon */}
          <div className="co-card">
            <div className="co-coupon-header">
              <p
                style={{
                  margin: 0,
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#9ca3af",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                Coupon Code
              </p>
              <button
                type="button"
                className="co-open-coupons-btn"
                onClick={openCouponsModal}
                disabled={!effectiveSellerId}
                title="Browse seller coupons"
              >
                <Percent size={14} />
              </button>
            </div>
            <div className="co-coupon">
              <input
                type="text"
                placeholder="Enter coupon code"
                value={coupon}
                onChange={(e) => {
                  setCoupon(e.target.value);
                  setCouponApplied(false);
                  setCouponPrice(0);
                }}
                disabled={couponApplied}
              />
              <button
                className={`co-coupon-btn ${couponApplied ? "applied" : "idle"}`}
                onClick={handleCoupon}
                disabled={isCouponLoading || couponApplied}
              >
                {isCouponLoading ? (
                  "…"
                ) : couponApplied ? (
                  <span
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    <CheckCircle size={13} /> Applied
                  </span>
                ) : (
                  "Apply"
                )}
              </button>
            </div>
          </div>

          {showCouponsModal &&
            typeof document !== "undefined" &&
            createPortal(
              <div className="co-modal-overlay" role="dialog" aria-modal="true">
                <div className="co-modal-card">
                  <div className="co-modal-header">
                    <h6>Available Coupons</h6>
                    <button
                      type="button"
                      onClick={() => setShowCouponsModal(false)}
                    >
                      Close
                    </button>
                  </div>

                  {sellerCouponsLoading ? (
                    <div className="co-modal-empty">Loading coupons…</div>
                  ) : sellerCoupons.length === 0 ? (
                    <div className="co-modal-empty">
                      No coupons available for this seller right now.
                    </div>
                  ) : (
                    <div className="co-coupons-list">
                      {sellerCoupons.map((offer, idx) => (
                        <button
                          key={offer._id || offer.code || idx}
                          type="button"
                          className="co-coupon-item"
                          onClick={() => handleSelectCoupon(offer.code)}
                          disabled={isCouponLoading}
                        >
                          <div>
                            <p className="co-coupon-item-code">{offer.code}</p>
                            <p className="co-coupon-item-meta">
                              {offer.type === "percentage"
                                ? `${offer.value || 0}% off`
                                : `${sym}${offer.value || 0} off`}
                              {Number(offer.minOrder) > 0
                                ? ` • Min order ${sym}${offer.minOrder}`
                                : ""}
                            </p>
                          </div>
                          <span className="co-coupon-item-action">Apply</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>,
              document.body,
            )}

          {/* Payment method */}
          <div className="co-card">
            <p
              style={{
                margin: "0 0 12px",
                fontSize: 11,
                fontWeight: 700,
                color: "#9ca3af",
                textTransform: "uppercase",
                letterSpacing: 0.5,
                fontFamily: "'Inter', sans-serif",
              }}
            >
              Payment Method
            </p>

            <label
              className={`co-pay-option ${paySystemName === "cashOnDelivery" ? "selected" : ""}`}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <input
                  type="radio"
                  name="pay"
                  value="cashOnDelivery"
                  checked={paySystemName === "cashOnDelivery"}
                  onChange={(e) => setPaySystemName(e.target.value)}
                />
                <div>
                  <p
                    style={{
                      margin: 0,
                      fontWeight: 700,
                      fontSize: 13,
                      color: "#111827",
                      fontFamily: "'Inter', sans-serif",
                    }}
                  >
                    Cash on Delivery
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 11,
                      color: "#9ca3af",
                      fontFamily: "'Inter', sans-serif",
                    }}
                  >
                    Pay when your order arrives
                  </p>
                </div>
              </div>
              <Banknote size={18} color="#059669" />
            </label>

            {isBangladesh ? (
              <label
                className={`co-pay-option ${paySystemName === "eps" ? "selected" : ""}`}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <input
                    type="radio"
                    name="pay"
                    value="eps"
                    checked={paySystemName === "eps"}
                    onChange={(e) => setPaySystemName(e.target.value)}
                  />
                  <div>
                    <p
                      style={{
                        margin: 0,
                        fontWeight: 700,
                        fontSize: 13,
                        color: "#111827",
                        fontFamily: "'Inter', sans-serif",
                      }}
                    >
                      Online Payment
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: 11,
                        color: "#9ca3af",
                        fontFamily: "'Inter', sans-serif",
                      }}
                    >
                      bKash, Nagad, Card via EPS
                    </p>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  <img
                    src="/Assets/Img/payment/png/Bkash.png"
                    alt="bKash"
                    style={{ height: 18 }}
                  />
                  <img
                    src="/Assets/Img/payment/png/Nagad.png"
                    alt="Nagad"
                    style={{ height: 18 }}
                  />
                </div>
              </label>
            ) : (
              <label
                className={`co-pay-option ${paySystemName === "stripe" ? "selected" : ""}`}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <input
                    type="radio"
                    name="pay"
                    value="stripe"
                    checked={paySystemName === "stripe"}
                    onChange={(e) => setPaySystemName(e.target.value)}
                  />
                  <div>
                    <p
                      style={{
                        margin: 0,
                        fontWeight: 700,
                        fontSize: 13,
                        color: "#111827",
                        fontFamily: "'Inter', sans-serif",
                      }}
                    >
                      Card Payment
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: 11,
                        color: "#9ca3af",
                        fontFamily: "'Inter', sans-serif",
                      }}
                    >
                      Visa, Mastercard via Stripe
                    </p>
                  </div>
                </div>
                <CreditCard size={18} color="#6366f1" />
              </label>
            )}
          </div>

          {/* Place order */}
          <button
            className="co-place-btn"
            onClick={handleSubmitOrder}
            disabled={
              isLoading || regionLoading || filteredCart.length === 0 || !addressId
            }
          >
            {isLoading ? (
              <>
                <Loader
                  size={16}
                  style={{ animation: "spin 1s linear infinite" }}
                />{" "}
                {btnLabel}
              </>
            ) : (
              <>
                <Truck size={16} /> {btnLabel}
              </>
            )}
          </button>

          <p
            style={{
              margin: 0,
              fontSize: 11,
              color: "#9ca3af",
              textAlign: "center",
              fontFamily: "'Inter', sans-serif",
              lineHeight: 1.6,
            }}
          >
            By placing your order you agree to our terms. Prices include
            applicable taxes.
          </p>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
